import { Link } from 'react-router-dom'

const guide = {
  slug: 'hardstop-alternative',
  category: 'compare',
  kicker: 'Compare · HardStop',
  title: 'HardStop alternative: same idea, a system-level lock — and what to compare before you pay',
  seoTitle: 'HardStop Alternative: A System-Level Lock, Compared',
  description: `HardStop and SELFCNTRL both lock the device instead of asking for a password. What that model means, seven questions to ask either of us, and where we differ.`,
  published: '2026-09-03',
  modified: '2026-09-26',
  minutes: 6,
  intro: `If you've found HardStop, you've already understood the important
          thing: a blocker that lives inside a permission you granted is not a
          blocker. HardStop and SELFCNTRL both start from that idea and lock
          the device itself. This page isn't a takedown — the model is right.
          It's the list of questions worth asking either product before you
          pay, and honest answers for ours.`,
  next: ['porn-blocker-you-cant-uninstall', 'supervised-iphone-explained', 'porn-blocker-with-a-delay'],
  body: (
    <>
      <h2>The model both products share</h2>
      <p>
        HardStop describes itself as blocking you can't turn off: the block
        is installed at the level of the device, so it isn't an app you
        delete or a passcode you know. SELFCNTRL works the same way on iPhone
        and iPad: the device is supervised, a configuration profile filters
        pornography by category at DNS level for every app, forbids adding a
        VPN, and cannot be removed from the phone. If you want the mechanics,
        they're in <Link to="/guides/supervised-iphone-explained">supervised iPhone explained</Link>.
      </p>
      <p>
        We only describe HardStop by what its own site says. Check it for
        current features, devices and pricing; anything more specific
        belongs to them, not to us.
      </p>

      <h2>Seven questions to ask any system-level blocker</h2>
      <ol>
        <li><b>Which devices get the real lock?</b> On iPhone and iPad, supervision makes a profile unremovable. On Mac and Windows, no equivalent exists for a personal computer; anyone claiming otherwise is describing a lock you administer. Our answer: iPhone and iPad are the immovable ones; Mac and Windows get the filter, curfews and the delay, honestly labelled.</li>
        <li><b>How do I get out, and how long does it take?</b> A lock you can never leave is a trap; a lock you can leave instantly is a toggle. Our answer: a request from your account, effective after twenty-four hours.</li>
        <li><b>Can I make it stricter without waiting?</b> If tightening waits too, you'll never tighten. Our answer: tightening is immediate, loosening waits, the floor never drops. Why that asymmetry is the whole design: <Link to="/guides/porn-blocker-with-a-delay">the 24-hour rule</Link>.</li>
        <li><b>What does it see?</b> A category filter needs no history. Our answer: nothing is read or stored, no screenshots, no report to anyone. It's in the <Link to="/confidentialite">privacy policy</Link>, with the processors we use.</li>
        <li><b>Do I need to involve someone?</b> Our answer: no. No accountability partner, no support ticket to unlock. <Link to="/guides/porn-blocker-without-accountability-partner">Why</Link>.</li>
        <li><b>What happens to the phone's data during setup?</b> Supervision can involve a reset. Our answer: back up first, restore after, about ten minutes, and the supervisor walks you through it. Any tool that promises "no erase" — back up anyway.</li>
        <li><b>What's the guarantee, exactly?</b> Read the conditions of any guarantee before relying on it. Ours: on a device we locked, strip the filter off by your own means and we send $100 on top of a full refund; plus thirty days refunded without justification. The conditions are one page: <Link to="/remboursement">refund policy</Link>.</li>
      </ol>

      <h2>Where SELFCNTRL is different</h2>
      <ul>
        <li><b>The one-way delay.</b> Tighten now, loosen in twenty-four hours, with a floor that never drops. It turns every good day into a chance to raise the bar and every bad night into a wait.</li>
        <li><b>A pace you choose once.</b> Straight cut, or a taper that climbs from four locked days to seven on its own.</li>
        <li><b>Night curfews.</b> The internet cut entirely in the hours relapses happen, per weekday, to the minute.</li>
        <li><b>Getting paid to hold.</b> CNTRL points every month the block stays on; 1,000 CNTRL buy a $5 gift card.</li>
        <li><b>The rest of the app.</b> Day counter, goals across five areas reset weekly, a short Sunday check-in. No sermon, no badges.</li>
      </ul>

      <h2>Where the honest answer is "it depends"</h2>
      <p>
        If another product covers a device we don't (we don't lock Android),
        or blocks something inside an app that we can only block at the
        domain level, that's a real reason to choose it. DNS filtering works
        on domains, not on individual posts, and we say so on every guide
        that touches Reddit or X. Pick the tool that shuts your actual door.
      </p>

      <h2>Side by side, on the model</h2>
      <table>
        <thead>
          <tr><th></th><th>App with a password</th><th>System-level lock (HardStop, SELFCNTRL)</th><th>SELFCNTRL specifically</th></tr>
        </thead>
        <tbody>
          <tr><td>Undone at 2am by</td><td>You, with the password</td><td>Nobody on the device</td><td>Nobody on the device; account request + 24 h</td></tr>
          <tr><td>Covers every app and browser</td><td>Depends</td><td>Yes (device level)</td><td>Yes, DNS category filter</td></tr>
          <tr><td>VPN workaround (iPhone)</td><td>Open</td><td>Closed if supervised</td><td>Closed</td></tr>
          <tr><td>Loosening</td><td>Instant</td><td>Per product</td><td>24 h, floor never drops</td></tr>
          <tr><td>Reads your activity</td><td>Depends</td><td>Per product</td><td>No</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Is SELFCNTRL the same thing as HardStop?',
      a: `Same model: a lock at the level of the device rather than a
          password. The details differ — the one-way 24-hour delay, the
          pace you choose once, curfews, rewards, and a written $100 bet
          with thirty-day refunds. Compare both sites and pick the door that
          fits.`
    },
    {
      q: 'Does either product work on Android?',
      a: `SELFCNTRL locks iPhone, iPad, Mac and Windows; not Android. Check
          HardStop's site for its current device list.`
    },
    {
      q: 'Which one is cheaper?',
      a: `Prices change; read both sites on the day. Ours is on the home
          page: $9.99 a month, or $49.99 a year.`
    },
    {
      q: 'Can I switch from HardStop to SELFCNTRL?',
      a: `Yes. Release the device from the previous product first, then
          run the SELFCNTRL supervisor: ten minutes, once, with a backup and
          restore. A device can only carry one supervising profile at a
          time.`
    }
  ]
}

export default guide
